import { hasPermission } from '#modules/permissions'
import type { UserIncidentUnit } from '@/contexts/UserContext'

/**
 * Route access registry - single source of truth for every route in the app.
 *
 * Enforced by RouteEnforcer on every navigation.
 * Used by nav components to gate item visibility.
 *
 * Adding a route:
 *   1. Add the path + access rule here.
 *   2. Add the <Route> in App.tsx (path → component).
 *   3. If it needs a nav item, add it to the NAV array in Sidebar.tsx.
 */

// -------------------------------------------------------------------------------------------------
// Types
// -------------------------------------------------------------------------------------------------

export type RouteAccess = {
  require?: 'any' | 'assigned'
  permissions?: string[]
}

export type AccessResult = 'ok' | 'denied' | 'unassigned' | 'unauthenticated'

export type AccessUser = {
  incidentUnit: UserIncidentUnit | null
  permissions: Set<string>
}
type RoutePath = keyof typeof ROUTES

// ── ID format validators ─────────────────────────────────────────────────────────────────────────

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const FACODE = /^[A-HJ-NP-Z2-9]{8}$/
// ics214_headers is keyed in the URL by its human-readable display id, not
// its internal UUID primary key — see createICS214() in
// src/lib/services/ics214.ts: `ICS214-${YYYYMMDD}-${UNITNAME_ALPHANUMERIC}-${seq3}`.
// This was previously validated against UUID, which no real ics214_id value
// ever matches, so /ics214/:ics214id and /ics214/:ics214id/activity always
// failed to match any route pattern and rendered NotFound (404) — the
// "Add Activity" link 404 reported 2026-07-19.
const ICS214ID = /^ICS214-\d{8}-[A-Z0-9]+-\d{3}$/

const PARAM_FORMATS: Record<string, RegExp> = {
  facode: FACODE,
  ectrid: UUID,
  unitid: UUID,
  incidentid: UUID,
  rosterid: UUID,
  marid: UUID,
  csid: UUID,
  supplyid: UUID,
  ics214id: ICS214ID,
  invid: UUID,
  formid: UUID,
  catid: UUID,
  docid: UUID
}

// -------------------------------------------------------------------------------------------------
// Registry
// -------------------------------------------------------------------------------------------------

/**
 * require:
 *   (omit)       - public, no auth needed
 *   'any'        - any authenticated user
 *   'assigned'   - active unit assignment required
 *
 * permissions:
 *   Without require → hard gate (must have at least one listed permission)
 *   With require    → escape valve (satisfies require OR has any listed permission)
 */
export const ROUTES = {

  // ── Public - no authentication required ────────────────────────────────────────────────────────
  '/login':                               {},
  '/change-password':                     {},
  // '/onboard':                             {}, // TODO: authenticated
  '/fire-admin/:facode':                  {},

  // ── Root - redirect to user home ───────────────────────────────────────────────────────────────
  '/':                                    { require: 'any' },

  // ── Any authenticated user ─────────────────────────────────────────────────────────────────────
  '/profile':                             { require: 'any' },
  '/roster':                              { require: 'any' },
  '/roster/:rosterid':                    { require: 'any' },
  '/schedule/request':                    { require: 'any' },
  '/chat':                                { require: 'any' },
  '/notifications':                       { require: 'any' },
  '/payroll/my':                          { require: 'any' },
  '/documents':                           { require: 'any' },
  '/documents/handbook':                  { require: 'any' },
  '/documents/:docid':                    { require: 'any' },

  // ── Field dashboard ────────────────────────────────────────────────────────────────────────────
  '/dashboard/my-unit':                   { require: 'assigned' },

  // ── Field operations ───────────────────────────────────────────────────────────────────────────
  '/incidents/:incidentid':               { require: 'assigned', permissions: ['incidents.view'] },
  '/incidents/:incidentid/shift-ticket':  { require: 'assigned', permissions: ['incidents.manage'] },

  '/ics214':                              { require: 'assigned', permissions: ['ics214.view'] },
  '/ics214/new':                          { require: 'assigned', permissions: ['ics214.manage'] },
  '/ics214/:ics214id':                    { require: 'assigned', permissions: ['ics214.view'] },
  '/ics214/:ics214id/activity':           { require: 'assigned', permissions: ['ics214.view'] },

  '/encounters':                          { require: 'assigned', permissions: ['encounters.view'] },
  '/encounters/new':                      { require: 'assigned', permissions: ['encounters.manage'] },
  '/encounters/new/simple':               { require: 'assigned', permissions: ['encounters.manage'] },
  '/encounters/new/pcr':                  { require: 'assigned', permissions: ['encounters.manage'] },
  '/encounters/:ectrid':                  { require: 'assigned', permissions: ['encounters.view'] },
  '/encounters/:ectrid/edit':             { require: 'assigned', permissions: ['encounters.manage'] },
  '/encounters/photos/new':               { require: 'assigned', permissions: ['encounters.manage'] },
  '/encounters/procedures/new':           { require: 'assigned', permissions: ['encounters.manage'] },
  '/unsigned-orders':                     { require: 'assigned', permissions: ['encounters.view'] },
  '/unsigned-pcrs':                       { require: 'assigned', permissions: ['encounters.view'] },
  '/unsigned-items':                      { require: 'assigned', permissions: ['encounters.view'] },
  '/patient-search':                      { require: 'assigned', permissions: ['encounters.view'] },
  '/consent/ama':                         { require: 'assigned', permissions: ['encounters.view'] },
  '/consent/treat':                       { require: 'assigned', permissions: ['encounters.view'] },
  '/comp-claims':                         { require: 'assigned', permissions: ['encounters.view'] },
  '/comp-claims/new':                     { require: 'assigned', permissions: ['encounters.manage'] },

  '/mar':                                 { require: 'assigned', permissions: ['mar.view'] },
  '/mar/new':                             { require: 'assigned', permissions: ['mar.manage'] },
  '/mar/:marid':                          { require: 'assigned', permissions: ['mar.view'] },
  '/mar/search':                          { require: 'assigned', permissions: ['mar.view'] },

  '/units/:unitid':                       { require: 'assigned', permissions: ['units.view'] },

  '/inventory':                           { require: 'assigned', permissions: ['inventory.view'] },
  '/inventory/add':                       { require: 'assigned', permissions: ['inventory.manage'] },
  '/inventory/:invid':                    { require: 'assigned', permissions: ['inventory.view'] },
  '/inventory/burnrate':                  { require: 'assigned', permissions: ['inventory.view'] },
  '/inventory/reorder':                   { require: 'assigned', permissions: ['inventory.manage'] },
  '/inventory/expiring':                  { require: 'assigned', permissions: ['inventory.view'] },

  '/cs':                                  { require: 'assigned', permissions: ['cs.view'] },
  '/cs/overview':                         { require: 'assigned', permissions: ['cs.view'] },
  '/cs/transfer':                         { require: 'assigned', permissions: ['cs.manage'] },
  '/cs/count':                            { require: 'assigned', permissions: ['cs.manage'] },
  '/cs/checklist':                        { require: 'assigned', permissions: ['cs.manage'] },
  '/cs/item/:csid':                       { require: 'assigned', permissions: ['cs.view'] },
  '/cs-inventory/count':                  { require: 'assigned', permissions: ['cs.manage'] },

  '/supply-runs':                         { require: 'assigned', permissions: ['supply_runs.view'] },
  '/supply-runs/new':                     { require: 'assigned', permissions: ['supply_runs.manage'] },
  '/supply-runs/:supplyid':               { require: 'assigned', permissions: ['supply_runs.view'] },

  // ── Admin operations ───────────────────────────────────────────────────────────────────────────
  '/roster/new':                          { permissions: ['roster.manage'] },
  '/roster/hr':                           { permissions: ['roster.manage'] },
  '/roster/pay-rates':                    { permissions: ['payroll.manage'] },
  '/incidents':                           { permissions: ['incidents.view'] },
  '/incidents/new':                       { permissions: ['incidents.manage'] },
  '/units':                               { permissions: ['units.view'] },
  '/units/new':                           { permissions: ['units.manage'] },
  '/formulary':                           { permissions: ['inventory.view'] },
  '/formulary/:formid':                   { permissions: ['inventory.view'] },
  '/catalog':                             { permissions: ['inventory.view'] },
  '/catalog/:catid':                      { permissions: ['inventory.view'] },
  '/cs/receive':                          { permissions: ['cs.manage'] },
  '/cs/audit':                            { permissions: ['cs.audit'] },
  '/schedule':                            { permissions: ['schedule.manage'] },
  '/schedule/calendar':                   { permissions: ['schedule.manage'] },
  '/schedule/generate':                   { permissions: ['schedule.manage'] },
  '/map':                                 { permissions: ['incidents.manage'] },
  '/analytics':                           { permissions: ['admin.analytics'] },
  '/billing':                             { permissions: ['billing.view'] },
  '/payroll':                             { permissions: ['payroll.view_all'] },
  '/contacts':                            { permissions: ['contacts.manage'] },
  '/documents/new':                       { permissions: ['admin.documents'] },
  '/admin':                               { permissions: ['admin.view'] },
  '/admin/announcements':                 { permissions: ['admin.push'] },
  '/admin/push-notifications':            { permissions: ['admin.push'] },
  '/admin/chat-requests':                 { permissions: ['roster.manage'] },
  '/admin/company':                       { permissions: ['admin.settings'] },
  '/admin/fire-dashboard':                { permissions: ['incidents.manage'] },
  '/admin/financial':                     { permissions: ['expenses.view'] },
  '/admin/roles':                         { permissions: ['admin.settings'] },

} satisfies Record<string, RouteAccess>

// -------------------------------------------------------------------------------------------------
// Access evaluation
// -------------------------------------------------------------------------------------------------

/** Evaluates a RouteAccess rule against the current user context. */
function evaluateAccess(route: RouteAccess, pathVars: Record<string, string>, user: AccessUser | null): AccessResult {
  // Public routes: no require and no permissions gate - always ok regardless of auth
  if (route.require === undefined && !route.permissions?.length) return 'ok'

  // All other routes require authentication
  if (!user) return 'unauthenticated'

  const { incidentUnit, permissions } = user
  const hasPerm = route.permissions?.some(p => hasPermission(permissions, p)) ?? false

  switch (route.require) {
    case undefined:
    case 'any': {
      // Permissions act as a hard gate when set; otherwise open to all authenticated users
      return route.permissions?.length && !hasPerm ? 'denied' : 'ok'
    }
    case 'assigned': {
      // Active incident-unit assignment required; permission-holders bypass
      if (hasPerm) return 'ok'
      if (!incidentUnit) return 'unassigned'
      const { unitid, incidentid } = pathVars
      if (unitid && unitid !== incidentUnit.unit_id)
        return 'denied'
      if (incidentid && incidentid !== incidentUnit.incident_id)
        return 'denied'
      return 'ok'
    }
    default: {
      const _: never = route.require
      return 'denied'
    }
  }
}

/**
 * Evaluates access for a relative path. Returns undefined for unregistered paths.
 *
 * Expects a relative path (pathname only - no scheme, host, or port).
 * Strips query strings (?), hash fragments (#), and path parameters (;) before
 * matching, so sub-paths like /incidents?status=Closed are handled correctly.
 * Safe to call with location.pathname or any href string.
 */
export function evaluatePathAccess(relativePath: string, user: AccessUser | null): AccessResult | undefined {
  const pathname = relativePath.split(/[?#;]/)[0]

  for (const pattern of Object.keys(ROUTES) as RoutePath[]) {
    let pathVars: Record<string, string> = {}
    const patternParts = pattern.split('/')
    const pathParts = pathname.split('/')
    if (patternParts.length !== pathParts.length)
      continue;
    let match = true;
    for (let i = 0; i < patternParts.length; ++i) {
      if (patternParts[i].startsWith(':')) {
        const varName = patternParts[i].slice(1)
        if (!PARAM_FORMATS[varName]?.test(pathParts[i])) {
          match = false
          break
        }
        if (pathVars[varName]) {
          if (pathVars[varName] !== pathParts[i])
            return undefined
        }
        else {
          pathVars[varName] = pathParts[i]
        }
      }
      else {
        if (patternParts[i] !== pathParts[i]) {
          match = false
          break
        }
      }
    }
    if (match) {
      return evaluateAccess(ROUTES[pattern], pathVars, user)
    }
  }

  return undefined
}

/** Predicate for evaluatePathAccess - returns true if the user can access the given path. */
export function canAccessPath(pathname: string, user: AccessUser | null): boolean {
  return evaluatePathAccess(pathname, user) === 'ok'
}
